import { Fragment, useEffect, useLayoutEffect, useRef } from 'react'

import { datum, eurDelta, eurT, liefermonat, MONATE_KURZ, quartal, tagMonat } from '../format'
import type { NotierungAuswahl } from '../pfade'
import { NOTIERUNGSTATUS } from '../texte'
import type { NotierungSpalte, NotierungTabelle, NotierungZelle } from '../typen'
import Delta from './Delta'

/**
 * Ein Blatt der Notierungsmappe, wie Frau Geigenmüller es in Excel führt: oben je Rohstoff ein
 * Block, darunter je Lieferant (Spalte) die Angaben aus ihrem Spaltenkopf - Ladeort, Qualität,
 * Transportzuschlag -, darunter „EKP netto", „inkl. Transport" (nur mit Zuschlag) und „zu Vortag".
 * Die Zeilen laufen Jahr für Jahr, Quartal für Quartal, Monat für Monat.
 *
 * Anders als in Excel rechnet „zu Vortag" das Backend (die Formeln der Mappen zeigten teils auf den
 * falschen Tag). Der günstigste Preis einer Zeile je Rohstoff und Qualität ist fett hinterlegt; ein
 * Wert, der nicht vom Stichtag stammt, steht grau mit „vom TT.MM." im Kopf.
 *
 * Eine echte HTML-Tabelle wie die Kladde: markieren und nach Excel kopieren geht.
 *
 * Kommt man aus dem Verlauf (`auswahl`), sind eine Spalte (Kopf mit „ausgewählt“, Rahmen) und die Zeilen
 * der Liefermonate hervorgehoben, und die Tabelle rollt die Spalte in den sichtbaren Bereich.
 */

type Teil = 'netto' | 'inkl' | 'delta'

interface Spaltenblock {
  spalte: NotierungSpalte
  teile: Teil[]
}

const TEILTITEL: Record<Teil, string> = { netto: 'EKP netto', inkl: 'inkl. Transport', delta: 'zu Vortag' }

/** „Ladeort Rotterdam · SG-Ware · +69 € Transport · für Riesa" - der Spaltenkopf der Excel in einer Zeile. */
function spaltenAngaben(s: NotierungSpalte): string {
  return [
    s.ladeort && `Ladeort ${s.ladeort}`,
    s.qualitaet && `${s.qualitaet}-Ware`,
    s.transport_eur ? `+${eurT(s.transport_eur).replace(/,00$/, '')} € Transport` : null,
    s.lieferort && `für ${s.lieferort}`,
  ]
    .filter(Boolean)
    .join(' · ')
}

export default function NotierungsTabelle({ tabelle, auswahl }: { tabelle: NotierungTabelle; auswahl?: NotierungAuswahl | null }) {
  const kopf1 = useRef<HTMLTableRowElement>(null)
  const kopf2 = useRef<HTMLTableRowElement>(null)
  const tabelleRef = useRef<HTMLTableElement>(null)

  // Die zweite Kopfzeile bricht je nach Breite auf zwei oder drei Zeilen um. Damit beim Scrollen
  // keine Kopfzeile über die andere rutscht, messen statt schätzen.
  useLayoutEffect(() => {
    const t = tabelleRef.current
    if (!t || !kopf1.current || !kopf2.current) return
    const setzen = () => {
      const h1 = kopf1.current?.offsetHeight ?? 0
      const h2 = kopf2.current?.offsetHeight ?? 0
      t.style.setProperty('--nz-z2', `${h1}px`)
      t.style.setProperty('--nz-z3', `${h1 + h2}px`)
    }
    setzen()
    if (typeof ResizeObserver === 'undefined') return
    const beobachter = new ResizeObserver(setzen)
    beobachter.observe(kopf1.current)
    beobachter.observe(kopf2.current)
    return () => beobachter.disconnect()
  }, [tabelle])

  // Die gewählte Spalte mittig in den freien Bereich rechts der festen Monatsspalten rollen, die erste
  // gewählte Zeile unter den Kopf (zwei Zeilen Luft davor). Nur beim Ankommen und bei neuem Stichtag.
  const monateSchluessel = auswahl?.monate.join(',') ?? ''
  useEffect(() => {
    const t = tabelleRef.current
    const rahmen = t?.parentElement
    if (!t || !rahmen || !auswahl) return
    const r = rahmen.getBoundingClientRect()
    const fest = t.querySelector<HTMLElement>('th.nz-ecke')?.offsetWidth ?? 0
    const kopf = t.querySelector<HTMLElement>('th.nz-wahl-kopf')
    if (kopf) {
      const k = kopf.getBoundingClientRect()
      const frei = rahmen.clientWidth - fest
      rahmen.scrollLeft += k.left - r.left - fest - Math.max(0, (frei - k.width) / 2)
    }
    const zeile = t.querySelector<HTMLElement>('tr.nz-wahl-zeile')
    if (zeile) {
      const z = zeile.getBoundingClientRect()
      rahmen.scrollTop += z.top - r.top - (t.tHead?.offsetHeight ?? 0) - 2 * z.height
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabelle, auswahl?.spalte, monateSchluessel])

  const spaltenJeId = new Map(tabelle.spalten.map((s) => [s.quelle_id, s]))
  const bloecke = tabelle.rohstoffe.map((r) => {
    const spalten: Spaltenblock[] = r.quellen
      .map((id) => spaltenJeId.get(id))
      .filter((s): s is NotierungSpalte => s !== undefined)
      .map((spalte) => ({ spalte, teile: spalte.transport_eur ? ['netto', 'inkl', 'delta'] : ['netto', 'delta'] }))
    return { rohstoff: r, spalten, breite: spalten.reduce((n, s) => n + s.teile.length, 0) }
  })
  const alleSpalten = bloecke.flatMap((b) => b.spalten)
  const breite = alleSpalten.reduce((n, s) => n + s.teile.length, 0)
  const gewaehlteMonate = new Set(auswahl?.monate ?? [])

  /** Klassen eines Teils der gewählten Spalte: Fläche, dazu Rahmen links am ersten und rechts am letzten Teil. */
  function wahl(b: Spaltenblock, teil: Teil): string {
    if (b.spalte.quelle_id !== auswahl?.spalte) return ''
    return ` nz-wahl${teil === b.teile[0] ? ' nz-wahl-anfang' : ''}${teil === b.teile[b.teile.length - 1] ? ' nz-wahl-ende' : ''}`
  }

  if (alleSpalten.length === 0) {
    return <p className="leer">Für diese Gruppe sind noch keine Spalten angelegt - unter Stammdaten · Spalten.</p>
  }

  // Jahre als Zwischenzeilen wie in der Mappe.
  const jahre = new Map<string, string[]>()
  for (const m of tabelle.monate) {
    const j = m.slice(0, 4)
    jahre.set(j, [...(jahre.get(j) ?? []), m])
  }

  function zelleRendern(b: Spaltenblock, teil: Teil, monat: string) {
    const s = b.spalte
    const z: NotierungZelle | undefined = tabelle.zellen[String(s.quelle_id)]?.[monat]
    const schluessel = `${s.quelle_id}-${teil}`
    const letzte = (teil === b.teile[b.teile.length - 1] ? ' nz-spaltenende' : '') + wahl(b, teil)
    if (!z) return <td key={schluessel} className={`zahl nz-${teil}${letzte}`} />
    const alt = z.notiert_am < tabelle.am ? ' nz-alt' : ''
    const altTitel = alt ? ` - notiert am ${datum(z.notiert_am)}` : ''
    if (z.status !== 'preis') {
      return (
        <td key={schluessel} className={`nz-${teil} nz-ohne-preis${alt}${letzte}`} title={teil === 'netto' ? `${NOTIERUNGSTATUS[z.status]}${altTitel}` : undefined}>
          {teil === 'netto' ? NOTIERUNGSTATUS[z.status] : ''}
        </td>
      )
    }
    const best = tabelle.bestpreis[`${s.rohstoff_id}|${s.qualitaet ?? ''}`]?.[monat]
    const bestHier = best?.quelle_id === s.quelle_id && teil === (s.transport_eur ? 'inkl' : 'netto')
    const bestTitel = bestHier
      ? `Bestpreis ${s.rohstoff}${s.qualitaet ? ` ${s.qualitaet}` : ''} für ${liefermonat(monat)}: ${eurT(best.preis_inkl)} €/t${s.transport_eur ? ' inkl. Transport' : ''}`
      : undefined
    if (teil === 'delta') {
      const titel =
        z.vortag_preis !== null
          ? `Vortag${s.vortag ? ` (${datum(s.vortag)})` : ''}: ${eurT(z.vortag_preis)} €/t, jetzt ${eurT(z.preis)} €/t - ${eurDelta(z.delta)} €/t`
          : 'kein früherer Wert für diesen Monat'
      return (
        <td key={schluessel} className={`zahl nz-delta${alt}${letzte}`} title={titel}>
          <Delta wert={z.delta} signal={z.signal} />
        </td>
      )
    }
    const wert = teil === 'netto' ? z.preis : z.preis_inkl
    return (
      <td key={schluessel} className={`zahl nz-${teil}${alt}${bestHier ? ' nz-best' : ''}${letzte}`} title={[bestTitel, altTitel.slice(3)].filter(Boolean).join(' · ') || undefined}>
        {eurT(wert)}
      </td>
    )
  }

  return (
    <div className="tabellenrahmen nz-rahmen">
      <table className="nz-tabelle" ref={tabelleRef}>
        <thead>
          <tr ref={kopf1}>
            <th className="nz-ecke" rowSpan={3} colSpan={2}>
              <div>Stichtag</div>
              <div className="nz-ecke-tag">{datum(tabelle.am)}</div>
              <div className="hinweis">EUR je Tonne</div>
            </th>
            {bloecke.map((b) =>
              b.breite === 0 ? null : (
                <th key={b.rohstoff.id} className="nz-z1 block" colSpan={b.breite}>
                  {b.rohstoff.name}
                </th>
              ),
            )}
          </tr>
          <tr ref={kopf2}>
            {alleSpalten.map((b) => {
              const s = b.spalte
              const alt = s.notiert_am !== null && s.notiert_am < tabelle.am
              const gewaehlt = s.quelle_id === auswahl?.spalte
              return (
                <th
                  key={s.quelle_id}
                  className={`nz-z2 nz-spaltenende${gewaehlt ? ' nz-wahl nz-wahl-anfang nz-wahl-ende nz-wahl-kopf' : ''}`}
                  colSpan={b.teile.length}
                  title={s.regel ?? undefined}
                >
                  {gewaehlt && <div className="nz-wahl-text">ausgewählt</div>}
                  <div className="nz-lieferant">{s.bezeichnung}</div>
                  <div className="nz-angaben">{spaltenAngaben(s)}</div>
                  {s.notiert_am === null ? (
                    <div className="nz-stand alt">keine Notierung</div>
                  ) : alt ? (
                    <div className="nz-stand alt" title={`Letzte Notierung am ${datum(s.notiert_am)} - am Stichtag kam nichts`}>
                      vom {tagMonat(s.notiert_am)}
                    </div>
                  ) : null}
                </th>
              )
            })}
          </tr>
          <tr>
            {alleSpalten.map((b) =>
              b.teile.map((t) => (
                <th key={`${b.spalte.quelle_id}-${t}`} className={`nz-z3 zahl${t === b.teile[b.teile.length - 1] ? ' nz-spaltenende' : ''}${wahl(b, t)}`}>
                  {TEILTITEL[t]}
                </th>
              )),
            )}
          </tr>
        </thead>
        <tbody>
          {[...jahre.entries()].map(([jahr, monate]) => (
            <Fragment key={jahr}>
              <tr className="nz-jahr">
                <td className="nz-quartal">{jahr}</td>
                <td className="nz-monat" />
                <td colSpan={breite} />
              </tr>
              {monate.map((m, i) => {
                const monat = Number(m.slice(5, 7))
                const quartalsanfang = i === 0 || (monat - 1) % 3 === 0
                const gewaehlt = gewaehlteMonate.has(m)
                const klassen = [(monat - 1) % 3 === 0 && 'nz-quartalsanfang', gewaehlt && 'nz-wahl-zeile'].filter(Boolean).join(' ')
                return (
                  <tr key={m} className={klassen || undefined}>
                    <td className="nz-quartal">{quartalsanfang ? quartal(m) : ''}</td>
                    <td className="nz-monat" title={gewaehlt ? `${liefermonat(m)} - ausgewählt im Verlauf` : undefined}>
                      {MONATE_KURZ[monat - 1]}
                    </td>
                    {alleSpalten.map((b) => b.teile.map((t) => zelleRendern(b, t, m)))}
                  </tr>
                )
              })}
            </Fragment>
          ))}
        </tbody>
      </table>
      {tabelle.monate.length === 0 && <p className="leer">An diesem Stichtag liegen für diese Gruppe keine Preise vor.</p>}
    </div>
  )
}
